import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  RectangleStackIcon,
  ClockIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { useVacancyStore, MainTabId } from '../../store/vacancyStore';
import { isRelevanterAvailable } from '../../ats/atsConfig';
import { useBrandingStore } from '../../store/brandingStore';

interface TabItem {
  id: MainTabId;
  label: string;
  to: string;
  Icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

const baseTabs: TabItem[] = [
  {
    id: 'vacancy' as MainTabId,
    label: 'Вакансия',
    to: '/recruiter/vacancy',
    Icon: RectangleStackIcon,
  },
  {
    id: 'search' as MainTabId,
    label: 'Поиск',
    to: '/recruiter/search',
    Icon: ClockIcon,
  },
  {
    id: 'dialogs' as MainTabId,
    label: 'Диалоги',
    to: '/recruiter/messenger',
    Icon: DocumentTextIcon,
  },
  {
    id: 'scheduled-interviews' as MainTabId,
    label: 'Собеседования',
    to: '/recruiter/interviews',
    Icon: CalendarIcon,
  },
  {
    id: 'analytics' as MainTabId,
    label: 'Итоги Подбора',
    to: '/recruiter/reports',
    Icon: ChartBarIcon,
  },
];

const isMailingsVacancyEditPath = (pathname: string, search: string): boolean => (
  pathname.startsWith('/recruiter/vacancy/edit-data/') &&
  new URLSearchParams(search).get('tab') === 'mailings'
);

const getTabIdFromPath = (pathname: string, search = ''): MainTabId | null => {
  if (isMailingsVacancyEditPath(pathname, search)) return 'mailings';
  if (pathname.includes('/vacancy/edit-questions/')) return 'vacancy';
  if (pathname.startsWith('/recruiter/vacancy')) return 'vacancy';
  if (pathname.startsWith('/recruiter/search')) return 'search';
  if (pathname.startsWith('/recruiter/mailings')) return 'mailings';
  if (pathname.startsWith('/recruiter/messenger')) return 'dialogs';
  if (pathname.startsWith('/recruiter/interviews')) return 'scheduled-interviews';
  if (pathname.startsWith('/recruiter/reports')) return 'analytics';
  if (pathname === '/ats' || pathname.startsWith('/ats/')) return 'ats';
  return null;
};

export const getTabPath = (tabId: MainTabId, selectedVacancyId: number | null): string => {
  const positionSearch = selectedVacancyId ? `?positionId=${selectedVacancyId}` : '';

  switch (tabId) {
    case 'vacancy':
      return selectedVacancyId ? `/recruiter/vacancy/${selectedVacancyId}` : '/recruiter/vacancy';
    case 'search':
      return `/recruiter/search${positionSearch}`;
    case 'mailings':
      return selectedVacancyId ? `/recruiter/vacancy/edit-data/${selectedVacancyId}?tab=mailings` : '/recruiter/mailings';
    case 'dialogs':
      return `/recruiter/messenger${positionSearch}`;
    case 'scheduled-interviews':
      return `/recruiter/interviews${positionSearch}`;
    case 'analytics':
      return '/recruiter/reports';
    case 'ats':
      return '/ats';
    default:
      return '/recruiter/vacancy';
  }
};

export function MainTabNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const colors = useBrandingColors();
  const { companyData, loadCompanyData } = useBrandingStore();
  const { 
    selectedVacancyId, 
    setLastActiveTabId
  } = useVacancyStore();

  useEffect(() => {
    if (!companyData) {
      loadCompanyData().catch(() => {});
    }
  }, [companyData, loadCompanyData]);

  // ATS is a separate product module. This navigation contains only the
  // workflow tabs of Relevanter; the module switcher owns the /ats entry.
  const tabs: TabItem[] = isRelevanterAvailable(companyData) ? baseTabs : [];

  useEffect(() => {
    const currentTabId = getTabIdFromPath(location.pathname, location.search);
    
    if (currentTabId) {
      setLastActiveTabId(currentTabId);
    }
  }, [location.pathname, location.search, setLastActiveTabId]);

  const isActive = (to: string, tabId: MainTabId) => {
    if (isMailingsVacancyEditPath(location.pathname, location.search)) {
      return tabId === 'mailings';
    }

    if (location.pathname.includes('/vacancy/edit-questions/')) {
      return tabId === 'vacancy';
    }

    return location.pathname.startsWith(to);
  };

  const getVacancyTabLink = () => {
    if (selectedVacancyId) {
      return `/recruiter/vacancy/${selectedVacancyId}`;
    }
    return '/recruiter/vacancy';
  };

  const handleTabClick = async (tab: TabItem, e: React.MouseEvent) => {
    if (tab.id === 'analytics') {
      window.dispatchEvent(new CustomEvent('analyticsTabClick'));
    }

    if (tab.id === 'vacancy') {
      e.preventDefault();
      navigate(getVacancyTabLink());
      return;
    }
  };

  return (
    <div className="bg-[#F5F6F1] border-b border-[#CDD0D5] px-8">
      <div className="flex items-center">
        {tabs.map((tab, index) => {
          const active = isActive(tab.to, tab.id);
          const to = tab.id === 'vacancy' ? getVacancyTabLink() : getTabPath(tab.id, selectedVacancyId);

          return (
            <React.Fragment key={tab.id}>
              <Link
                to={to}
                data-testid={`main-tab-${tab.id}`}
                onClick={(e) => handleTabClick(tab, e)}
                className={`flex items-center gap-2 py-3 px-4 text-[14px] font-inter font-medium transition-colors ${
                  active
                    ? 'border-b-2'
                    : 'text-[var(--text-medium)] hover:text-[var(--text-dark)]'
                }`}
                style={
                  active
                    ? {
                        color: colors.primary,
                        borderBottomColor: colors.primary,
                      }
                    : undefined
                }
              >
                <tab.Icon className="w-4 h-4" />
                <span data-testid={`main-tab-${tab.id}-label`}>{tab.label}</span>
                {tab.badge !== undefined && (
                  <span className={active ? '' : 'text-[var(--text-light)]'}>
                    {tab.badge}
                  </span>
                )}
              </Link>
              {index < tabs.length - 1 && (
                <img src="/icons/delimiter.png" alt="" className="h-4" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
